/** Wire translation only: optional prompt bindings are not capability admission. */
type WireObject = Record<string, unknown>;
const object = (value: unknown): WireObject => (
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as WireObject : {}
);
const objects = (value: unknown): WireObject[] => Array.isArray(value) ? value.map(object) : [];
const hasExplicitBinding = (value: WireObject) => ['skill_id', 'selected_capability_id']
  .some(key => Object.prototype.hasOwnProperty.call(value, key));

const normalizeAction = (value: unknown, selected: WireObject[], resultId: string): unknown => {
  const action = object(value);
  const payload = object(action.payload);
  const recommendation = object(payload.recommendation);
  const candidate = selected.find(item => item.semantic_key === recommendation.semantic_key
    && item.prompt === payload.prefill_prompt);
  const optionalPromptBinding = [
    action.source === 'accepted_result_recommendation',
    action.surface === 'contextual_next_action',
    action.command === 'send_message', payload.command === 'send_message',
    action.capability_id === '', !hasExplicitBinding(action), !hasExplicitBinding(payload),
    !Object.prototype.hasOwnProperty.call(payload, 'capability_id'),
    recommendation.source_result_id === resultId,
    recommendation.execution_authorized === false,
    candidate?.kind === 'prompt', candidate?.capability_id === '', candidate?.operation == null,
    typeof candidate?.semantic_key === 'string' && candidate.semantic_key.length > 0,
    typeof candidate?.prompt === 'string' && candidate.prompt.length > 0,
  ].every(Boolean);
  if (!optionalPromptBinding) return value;
  const normalized = { ...action };
  delete normalized.capability_id;
  return normalized;
};

/** Preserve canonical evidence and all unknown fields; only normalize a proven optional wire field. */
export const normalizeRecommendationPayload = <T,>(value: T): T => {
  const payload = object(value);
  const resolution = object(payload.next_action_recommendation);
  const accepted = object(payload.accepted_semantic_result);
  const resultId = resolution.result_id;
  if (resolution.status !== 'selected' || typeof resultId !== 'string' || !resultId
      || resultId !== accepted.result_id || !Array.isArray(payload.next_actions)) return value;
  const selected = objects(resolution.selected);
  const next_actions = payload.next_actions.map(action => normalizeAction(action, selected, resultId));
  if (next_actions.every((action, index) => action === (payload.next_actions as unknown[])[index])) return value;
  return { ...payload, next_actions } as T;
};
